import type { Command } from './commands';
import { tileCenter, unitsToTile } from './fixed';
import { Rng } from './rng';
import type { BuildingDef, UnitDef } from '../data/types';
import { isComplete, type Building } from '../entities/building';
import { NEUTRAL_OWNER, type Player } from '../entities/player';
import type { Unit } from '../entities/unit';
import { EntityGrid } from '../map/entityGrid';
import { inBounds, isBlocked, setBlocked, type GameMap } from '../map/gameMap';
import { Pathfinder, type TilePoint } from '../map/pathfinding';
import { UnitGrid, type OccupancyFn } from '../map/unitGrid';
import {
  DYING_TICKS,
  beginAttack,
  stepAutoAcquire,
  stepCombat,
  stepDying,
} from '../systems/combat';
import {
  beginBuild,
  placementRefusal,
  stepConstruction,
  type PlacementRefusal,
} from '../systems/construction';
import { beginGather, stepGathering } from '../systems/gathering';
import { planPath, stepMovement } from '../systems/movement';
import {
  cancelLastTrain,
  enqueueTrain,
  stepProduction,
  trainRefusal,
  type TrainRefusal,
} from '../systems/production';

export const TICKS_PER_SECOND = 20;
export const MS_PER_TICK = 1000 / TICKS_PER_SECOND;

export interface SimOptions {
  /** Unit definitions by id, needed to resolve Train commands. */
  readonly unitDefs?: ReadonlyMap<string, UnitDef> | undefined;
  /** Building definitions by id, needed to resolve Build commands. */
  readonly buildingDefs?: ReadonlyMap<string, BuildingDef> | undefined;
}

/**
 * The deterministic, fixed-tick game simulation (CLAUDE.md §4.2).
 * Same map + seed + commands ⇒ identical results, on any engine.
 */
export class Sim {
  readonly map: GameMap;
  readonly rng: Rng;
  readonly pathfinder: Pathfinder;
  readonly grid: EntityGrid;
  readonly unitGrid: UnitGrid;
  private currentTick = 0;
  private nextEntityId = 1;
  private readonly unitsById = new Map<number, Unit>();
  private readonly buildingsById = new Map<number, Building>();
  private readonly playersById = new Map<number, Player>();
  private readonly unitDefs: ReadonlyMap<string, UnitDef>;
  private readonly buildingDefs: ReadonlyMap<string, BuildingDef>;
  private pending: Command[] = [];

  constructor(map: GameMap, seed: number, options: SimOptions = {}) {
    this.map = map;
    this.rng = new Rng(seed);
    this.pathfinder = new Pathfinder(map);
    this.grid = new EntityGrid(map.width, map.height);
    this.unitGrid = new UnitGrid(map.width, map.height);
    this.unitDefs = options.unitDefs ?? new Map();
    this.buildingDefs = options.buildingDefs ?? new Map();
  }

  get tick(): number {
    return this.currentTick;
  }

  player(owner: number): Player {
    let p = this.playersById.get(owner);
    if (!p) {
      p = { id: owner, gold: 0, lumber: 0, supplyUsed: 0 };
      this.playersById.set(owner, p);
    }
    return p;
  }

  /** Supply cap: the sum of supplyProvided over the owner's completed buildings. */
  supplyCap(owner: number): number {
    let cap = 0;
    for (const b of this.buildingsById.values()) {
      if (b.owner === owner && isComplete(b)) cap += b.def.supplyProvided;
    }
    return cap;
  }

  /** Why `def` cannot be placed at (x, y) for `owner` right now, or null when it can. */
  canPlace(owner: number, def: BuildingDef, x: number, y: number): PlacementRefusal | null {
    return placementRefusal(this, owner, def, x, y);
  }

  buildingDef(id: string): BuildingDef | undefined {
    return this.buildingDefs.get(id);
  }

  /** Why `b` cannot start training `def` right now, or null when it can. */
  canTrain(b: Building, def: UnitDef): TrainRefusal | null {
    return trainRefusal(this, b, def);
  }

  unitDef(id: string): UnitDef | undefined {
    return this.unitDefs.get(id);
  }

  spawnUnit(owner: number, def: UnitDef, tileX: number, tileY: number): Unit {
    if (isBlocked(this.map, tileX, tileY)) {
      throw new Error(`spawnUnit: tile (${tileX},${tileY}) is blocked or outside the map`);
    }
    const x = tileCenter(tileX);
    const y = tileCenter(tileY);
    const unit: Unit = {
      id: this.nextEntityId++,
      owner,
      def,
      hp: def.hp,
      x,
      y,
      prevX: x,
      prevY: y,
      path: null,
      carrying: null,
      hidden: false,
      state: { kind: 'idle' },
    };
    this.unitsById.set(unit.id, unit);
    this.unitGrid.add(unit);
    return unit;
  }

  /** A unit reaches 0 hp: it lingers briefly (fade-out), then is removed. Supply is refunded now. */
  killUnit(unit: Unit): void {
    if (unit.state.kind === 'dying') return;
    unit.hp = 0;
    unit.path = null;
    unit.hidden = false;
    unit.carrying = null;
    unit.state = { kind: 'dying', ticksLeft: DYING_TICKS };
    this.player(unit.owner).supplyUsed -= unit.def.supply;
  }

  /** A building reaches 0 hp: its tiles reopen and it is gone. Its queue is lost (no refund). */
  destroyBuilding(b: Building): void {
    if (!this.buildingsById.has(b.id)) return;
    for (const entry of b.queue) this.player(b.owner).supplyUsed -= entry.def.supply;
    b.queue.length = 0;
    for (let ty = b.y; ty < b.y + b.def.height; ty++) {
      for (let tx = b.x; tx < b.x + b.def.width; tx++) setBlocked(this.map, tx, ty, false);
    }
    this.grid.clear(b.x, b.y, b.def.width, b.def.height);
    this.buildingsById.delete(b.id);
  }

  /** Standing units other than `unit`, as pathfinding obstacles. */
  occupancyFor(unit: Unit): OccupancyFn {
    return (tx, ty) => {
      const id = this.unitGrid.standingAt(tx, ty);
      return id !== 0 && id !== unit.id;
    };
  }

  /**
   * Place a building with its top-left footprint tile at (tileX, tileY), complete.
   * Fails loudly on overlap. Player-built sites go through `placeSite`.
   */
  spawnBuilding(owner: number, def: BuildingDef, tileX: number, tileY: number, gold = 0): Building {
    return this.addBuilding(owner, def, tileX, tileY, gold, null);
  }

  /** Pay for and place an unfinished site. Caller checks `canPlace` first. */
  placeSite(owner: number, def: BuildingDef, tileX: number, tileY: number): Building {
    const p = this.player(owner);
    p.gold -= def.cost.gold;
    p.lumber -= def.cost.lumber;
    return this.addBuilding(
      owner,
      def,
      tileX,
      tileY,
      0,
      def.buildTicks > 0 ? { ticksLeft: def.buildTicks } : null,
    );
  }

  private addBuilding(
    owner: number,
    def: BuildingDef,
    tileX: number,
    tileY: number,
    gold: number,
    construction: { ticksLeft: number } | null,
  ): Building {
    for (let ty = tileY; ty < tileY + def.height; ty++) {
      for (let tx = tileX; tx < tileX + def.width; tx++) {
        if (!inBounds(this.map, tx, ty) || isBlocked(this.map, tx, ty)) {
          throw new Error(
            `spawnBuilding(${def.id}): tile (${tx},${ty}) is blocked or outside the map`,
          );
        }
      }
    }
    const building: Building = {
      id: this.nextEntityId++,
      owner,
      def,
      x: tileX,
      y: tileY,
      gold,
      queue: [],
      construction,
      hp: def.hp ?? null,
    };
    for (let ty = tileY; ty < tileY + def.height; ty++) {
      for (let tx = tileX; tx < tileX + def.width; tx++) setBlocked(this.map, tx, ty, true);
    }
    this.grid.place(building.id, tileX, tileY, def.width, def.height);
    this.buildingsById.set(building.id, building);
    return building;
  }

  spawnMine(def: BuildingDef, tileX: number, tileY: number, gold: number): Building {
    if (def.mine !== 'gold') throw new Error(`spawnMine: ${def.id} is not a gold mine`);
    return this.spawnBuilding(NEUTRAL_OWNER, def, tileX, tileY, gold);
  }

  getUnit(id: number): Unit | undefined {
    return this.unitsById.get(id);
  }

  getBuilding(id: number): Building | undefined {
    return this.buildingsById.get(id);
  }

  buildingAt(tileX: number, tileY: number): Building | undefined {
    if (!inBounds(this.map, tileX, tileY)) return undefined;
    const id = this.grid.at(tileX, tileY);
    return id === 0 ? undefined : this.buildingsById.get(id);
  }

  /** Units in stable id order (CLAUDE.md §4.2: iterate in a defined order). */
  units(): Unit[] {
    return [...this.unitsById.values()].sort((a, b) => a.id - b.id);
  }

  buildings(): Building[] {
    return [...this.buildingsById.values()].sort((a, b) => a.id - b.id);
  }

  /** True when a unit is on the tile (as of the start of this tick, plus spawns since). */
  unitOnTile(tx: number, ty: number, includeHidden = false): boolean {
    if (!inBounds(this.map, tx, ty)) return false;
    return includeHidden
      ? this.unitGrid.countAt(tx, ty) > 0
      : this.unitGrid.visibleCountAt(tx, ty) > 0;
  }

  /** Nearest tile within `radius` that is open and has no standing unit; ring by ring, fixed order. */
  freeTileNear(cx: number, cy: number, radius: number, self: Unit): TilePoint | null {
    for (let ring = 1; ring <= radius; ring++) {
      for (let ty = cy - ring; ty <= cy + ring; ty++) {
        for (let tx = cx - ring; tx <= cx + ring; tx++) {
          const onRing = Math.max(Math.abs(tx - cx), Math.abs(ty - cy)) === ring;
          if (!onRing || isBlocked(this.map, tx, ty)) continue;
          const id = this.unitGrid.standingAt(tx, ty);
          if (id === 0 || id === self.id) return { x: tx, y: ty };
        }
      }
    }
    return null;
  }

  /**
   * Nearest open tile around a building's footprint (ring by ring, fixed scan order)
   * that no unit is standing on, for spawning units next to it. Returns null when none.
   */
  freeTileAround(b: Building, maxRing = 3): TilePoint | null {
    for (let ring = 1; ring <= maxRing; ring++) {
      const x0 = b.x - ring;
      const y0 = b.y - ring;
      const x1 = b.x + b.def.width - 1 + ring;
      const y1 = b.y + b.def.height - 1 + ring;
      for (let ty = y0; ty <= y1; ty++) {
        for (let tx = x0; tx <= x1; tx++) {
          const onRing = tx === x0 || tx === x1 || ty === y0 || ty === y1;
          if (onRing && !isBlocked(this.map, tx, ty) && !this.unitOnTile(tx, ty))
            return { x: tx, y: ty };
        }
      }
    }
    return null;
  }

  /** Queue a command. Commands stamped with a past tick apply on the next tick. */
  enqueue(command: Command): void {
    this.pending.push(command);
  }

  /** Advance exactly one tick. */
  step(): void {
    const units = this.units();
    for (const unit of units) {
      unit.prevX = unit.x;
      unit.prevY = unit.y;
    }
    this.unitGrid.rebuild(units);

    const due = this.pending.filter((c) => c.tick <= this.currentTick);
    this.pending = this.pending.filter((c) => c.tick > this.currentTick);
    for (const command of due) this.apply(command);

    for (const unit of units)
      stepMovement(unit, this.map, this.pathfinder, this.occupancyFor(unit));

    for (const unit of units) {
      if (unit.state.kind === 'dying') {
        if (stepDying(unit)) this.unitsById.delete(unit.id);
        continue;
      }
      if (unit.state.kind === 'moving' && !unit.path) this.finishMove(unit);
      else if (unit.state.kind === 'gathering') stepGathering(this, unit);
      else if (unit.state.kind === 'building') stepConstruction(this, unit);
      else if (unit.state.kind === 'attacking') stepCombat(this, unit);
      if (unit.state.kind === 'idle') stepAutoAcquire(this, unit);
      if (unit.state.kind === 'idle' && !unit.hidden) this.settle(unit);
    }

    for (const b of this.buildings()) stepProduction(this, b);

    this.currentTick++;
  }

  /**
   * A move's path has run out. If the unit stopped short because someone was standing on
   * the goal tile when it set out, and that tile is free now, carry on to it; otherwise idle.
   */
  private finishMove(unit: Unit): void {
    if (unit.state.kind !== 'moving') return;
    const { targetX, targetY } = unit.state;
    const gx = Math.max(0, Math.min(this.map.width - 1, unitsToTile(targetX)));
    const gy = Math.max(0, Math.min(this.map.height - 1, unitsToTile(targetY)));
    const here = unitsToTile(unit.x) === gx && unitsToTile(unit.y) === gy;
    if (!here && !isBlocked(this.map, gx, gy) && this.unitGrid.standingAt(gx, gy) === 0) {
      const path = planPath(unit, this.map, this.pathfinder, targetX, targetY, {
        occupancy: this.occupancyFor(unit),
      });
      const end = path?.waypoints[path.waypoints.length - 1];
      if (path && end && unitsToTile(end.x) === gx && unitsToTile(end.y) === gy) {
        unit.path = path;
        return;
      }
    }
    unit.state = { kind: 'idle' };
  }

  /**
   * An idle unit sharing its tile with another standing unit steps to the nearest free
   * tile, so arrivals fan out instead of stacking. Lowest id keeps the tile.
   */
  private settle(unit: Unit): void {
    const tx = unitsToTile(unit.x);
    const ty = unitsToTile(unit.y);
    const owner = this.unitGrid.standingAt(tx, ty);
    if (owner === 0 || owner === unit.id) return;
    const free = this.freeTileNear(tx, ty, 4, unit);
    if (!free) return;
    const x = tileCenter(free.x);
    const y = tileCenter(free.y);
    unit.path = planPath(unit, this.map, this.pathfinder, x, y, {
      occupancy: this.occupancyFor(unit),
    });
    if (unit.path) unit.state = { kind: 'moving', targetX: x, targetY: y };
  }

  private apply(command: Command): void {
    if (command.kind === 'train' || command.kind === 'cancelTrain') {
      const b = this.buildingsById.get(command.buildingId);
      if (!b) return;
      if (command.kind === 'train') {
        const def = this.unitDefs.get(command.unitDefId);
        if (def) enqueueTrain(this, b, def);
      } else {
        cancelLastTrain(this, b);
      }
      return;
    }
    const unit = this.unitsById.get(command.unitId);
    if (!unit || unit.state.kind === 'dying') return; // the unit may have died since the command was issued
    switch (command.kind) {
      case 'move':
        unit.hidden = false;
        unit.path = planPath(unit, this.map, this.pathfinder, command.x, command.y, {
          occupancy: this.occupancyFor(unit),
        });
        unit.state = unit.path
          ? { kind: 'moving', targetX: command.x, targetY: command.y }
          : { kind: 'idle' };
        break;
      case 'stop':
        unit.hidden = false;
        unit.path = null;
        unit.state = { kind: 'idle' };
        break;
      case 'gather':
        beginGather(this, unit, command.target);
        break;
      case 'build': {
        const def = this.buildingDefs.get(command.buildingDefId);
        if (!def || !unit.def.builds.includes(def.id)) return;
        if (placementRefusal(this, unit.owner, def, command.x, command.y) !== null) return;
        const site = this.placeSite(unit.owner, def, command.x, command.y);
        if (isComplete(site)) return; // zero build time: nothing to walk to
        beginBuild(this, unit, site);
        break;
      }
      case 'resumeBuild': {
        const site = this.buildingsById.get(command.buildingId);
        if (site) beginBuild(this, unit, site);
        break;
      }
      case 'attack':
        beginAttack(this, unit, command.targetId);
        break;
    }
  }
}
